import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { Button, Card, Heading, Text } from '@ui/themed';
import { t } from 'i18next';
import { INTERCOM_TRIGGER } from '../../../constants/intercom';
import { PLAN_KEYS } from '../constants';
import { useInitiateBillingPlanFlow } from '../hooks/use-initiate-billing-plan-flow';
import { formatPlanPrice } from '../lib/format-price';
import type { PlanPoint } from '../types/plan';
import { PlanCardBullets } from './plan-card-bullets';

export type PlanCardProps = {
  planKey: string;
  planName: string;
  cost: number | null;
  description: string;
  bulletsHeading: string;
  bullets: PlanPoint[];
  canTrial?: boolean;
  currentPlanKey?: string;
  showNeedMore: boolean;
};

export function PlanCard({
  planKey,
  planName,
  cost,
  description,
  bulletsHeading,
  bullets,
  canTrial,
  currentPlanKey,
  showNeedMore,
}: PlanCardProps) {
  const { initiateBillingPlanFlow, isInitiating, isOtherPlanInitiating } =
    useInitiateBillingPlanFlow({ planKey, planName, planPrice: cost });

  const isCurrentPlan = currentPlanKey === planKey;
  const isFree = cost === 0;
  const isEnterprise = planKey === PLAN_KEYS.enterprise;
  const priceLabel = formatPlanPrice(cost);

  function planButtonText(): string {
    if (isCurrentPlan) return t('features:billing.components.plan_card.buttons.current_plan');
    if (isFree) return t('features:billing.components.plan_card.buttons.get_started');
    if (canTrial) return t('features:billing.components.plan_card.buttons.start_free_trial');
    return t('features:billing.components.plan_card.buttons.select_plan');
  }

  return (
    <Card>
      <Card.Body>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <Text variant="secondary">{planName}</Text>
            <Heading level="h2" style="xl">
              {priceLabel}
            </Heading>
            <div className="flex flex-col gap-1">
              {isFree ? (
                <Text variant="secondary">
                  {t('features:billing.components.plan_card.price.free_forever')}
                </Text>
              ) : (
                <Text variant="secondary">
                  {t('features:billing.components.plan_card.price.usd_per_month')}
                  {!isEnterprise &&
                    `, ${t('features:billing.components.plan_card.price.free_trial', { days: 14 })}`}
                </Text>
              )}
            </div>
          </div>

          <Text>{description}</Text>

          {isEnterprise ? (
            <span className={cn({ [INTERCOM_TRIGGER.enterpriseChat]: !isCurrentPlan })}>
              <Button variant="secondary" disabled={isCurrentPlan}>
                {isCurrentPlan
                  ? t('features:billing.components.plan_card.buttons.current_plan')
                  : t('features:billing.components.plan_card.buttons.speak_with_our_team')}
              </Button>
            </span>
          ) : (
            <Button
              variant="primary"
              disabled={isCurrentPlan || isOtherPlanInitiating}
              loading={isInitiating}
              onClick={() => initiateBillingPlanFlow()}
            >
              {planButtonText()}
            </Button>
          )}

          <div className="flex flex-col gap-2">
            {bulletsHeading && <Text bold>{bulletsHeading}</Text>}
            <PlanCardBullets points={bullets} showNeedMore={showNeedMore} />
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
